<script setup lang="ts">
import { defineAsyncComponent } from "vue";
import {
  emptyProductCatalog,
  fetchProductCatalog,
} from "~/composables/useProductCatalog";

const ProductGridTemplate1 = defineAsyncComponent(() => import("~/components/products/productGridTemplate1.vue"));
const ProductGridTemplate2 = defineAsyncComponent(() => import("~/components/products/productGridTemplate2.vue"));
interface propInterface {
    id:number
    categories:string|number
    order:string
    limit:number
    showHeader:boolean
    showFooter:boolean
    headerText:string;
    btnText:string;
    btnHref:string;
    design:string
}
const props=defineProps<{
    config: propInterface,
    class?:string,
}>()

const sectionSort = computed(() => ({
  latest: "id:desc",
  populars: "views:desc",
  discounts: "discount:desc",
  random: "random",
}[props.config.order] || "id:desc"));
const sectionLimit = computed(() => Math.max(1, Math.min(Number(props.config.limit) || 8, 96)));
const { locale } = useI18n();
const auth = useAuthStore();
const request = computed(() => ({
      category_slug: String(props.config.categories || ""),
      page: 1,
      per_page: sectionLimit.value,
      sort: sectionSort.value,
      include_facets: false,
      include_category_details: false,
}));
const key = computed(() => `section-products:${locale.value}:${JSON.stringify(request.value)}`);
const { data, status, refresh } = await useAsyncData(key, () => fetchProductCatalog(request.value), {
  default: () => emptyProductCatalog(sectionLimit.value),
});
const loading = computed(() => status.value === 'pending' && !data.value?.list?.length);
// SSR contains public prices. Refresh customer-specific prices after hydration or a session change.
onNuxtReady(() => {
  if (auth.isAuthenticated) void refresh();
});
onMounted(() => {
  watch(() => auth.isAuthenticated, () => { void refresh(); });
});

const header=computed(()=>props.config.headerText || data.value?.category?.name || '');
</script>

<template>

  <div :class="props?.class || ''">
    <el-skeleton v-if="loading" :rows="2" animated class="py-3" />

    <template v-else-if="data?.list?.length">
      <NuxtLinkLocale :to="data?.category?.url || props.config.btnHref || ''">
        <h2 v-if="props.config.showHeader && header" class="section_title mb-5">
          {{ header }}
        </h2>
      </NuxtLinkLocale>
      <productGridTemplate1
        v-if="!props.config?.design || props.config?.design === 'template1'"
        :list="data.list"
        :all-options="data.product_options"
      />
      <productGridTemplate2 v-if="props.config.design === 'template2'" :list="data.list" />
      <NuxtLinkLocale
        v-if="props.config.showFooter"
        :to="props.config.btnHref || data?.category?.url || ''"
      >
        <div class="d-flex justify-content-center">
          <div class="el-button el-button--primary el-button--default is-round">
            {{ props.config.btnText }}
          </div>
        </div>
      </NuxtLinkLocale>
    </template>
  </div>
</template>
